import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Alert } from 'react-bootstrap';
import FacilityDependencySection from './FacilityDependencySection';

const DeactivationReplacementModal = ({
  show,
  onCancel,
  onConfirm,
  dependencies,
  serverErrors = [],
  staleCandidateBanner = false
}) => {
  const [selections, setSelections] = useState({ nurse: {}, case_manager: {} });

  useEffect(() => {
    if (!show) {
      setSelections({ nurse: {}, case_manager: {} });
    }
  }, [show]);

  const handleSelectionChange = (type, patientId, value) => {
    setSelections(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [patientId]: value
      }
    }));
  };

  const isConfirmDisabled = useMemo(() => {
    if (!dependencies || !dependencies.facilities) return true;

    let disabled = false;
    dependencies.facilities.forEach(facility => {
      facility.patients.forEach(patient => {
        const noCandidates =
          !patient.replacement_candidates ||
          patient.replacement_candidates.length === 0;

        if (noCandidates) {
          disabled = true;
        }

        if (
          ['nurse', 'both'].includes(patient.dependency_type) &&
          !selections.nurse[patient.patient_id]
        ) {
          disabled = true;
        }

        if (
          ['case_manager', 'both'].includes(patient.dependency_type) &&
          !selections.case_manager[patient.patient_id]
        ) {
          disabled = true;
        }
      });
    });

    return disabled;
  }, [dependencies, selections]);

  const handleConfirm = () => {
    const payload = {
      nurse: [],
      case_manager: []
    };

    Object.keys(selections.nurse).forEach(patientId => {
      payload.nurse.push({
        patient_id: parseInt(patientId, 10),
        new_user_id: parseInt(selections.nurse[patientId], 10)
      });
    });

    Object.keys(selections.case_manager).forEach(patientId => {
      payload.case_manager.push({
        patient_id: parseInt(patientId, 10),
        new_user_id: parseInt(selections.case_manager[patientId], 10)
      });
    });

    onConfirm(payload);
  };

  if (!dependencies) return null;

  return (
    <Modal
      show={show}
      onHide={onCancel}
      backdrop="static"
      keyboard={false}
      size="lg"
      centered
    >
      <Modal.Header closeButton className="px-4 py-3">
        <Modal.Title as="h5" className="mb-0 fs-8 text-900">
          Deactivate & Reassign
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="px-4 py-3">
        <p className="text-700 fs-10 mb-3">
          This user is the assigned nurse or case manager for the patients
          below. Pick a replacement for each before deactivation can proceed.
        </p>

        {staleCandidateBanner && (
          <Alert variant="warning" className="fs-10 px-3 py-2 mb-3">
            Some replacements are no longer available - please review.
          </Alert>
        )}

        {dependencies.facilities.map(facility => (
          <FacilityDependencySection
            key={facility.facility_id}
            facility={facility}
            selections={selections}
            onSelectionChange={handleSelectionChange}
            errors={serverErrors}
          />
        ))}
      </Modal.Body>
      <Modal.Footer className="px-4 py-3 bg-light">
        <Button variant="outline-secondary" size="sm" onClick={onCancel}>
          Cancel - Keep User Active
        </Button>
        <Button
          variant="danger"
          size="sm"
          onClick={handleConfirm}
          disabled={isConfirmDisabled}
        >
          Deactivate & Reassign
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

DeactivationReplacementModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  dependencies: PropTypes.shape({
    facilities: PropTypes.arrayOf(
      PropTypes.shape({
        facility_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        patients: PropTypes.arrayOf(PropTypes.object)
      })
    )
  }),
  serverErrors: PropTypes.array,
  staleCandidateBanner: PropTypes.bool
};

export default DeactivationReplacementModal;
